import type { ComponentProps } from "react";
import { createContext, useContext } from "react";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

const styles = tv({
  slots: {
    root: "@container flex grow rounded-2xl",
    container:
      "flex grow flex-col p-4 @lg:grid @lg:gap-x-6 @lg:gap-y-2 @lg:p-5 @lg:has-[.content]:has-[.header]:grid-rows-[auto_1fr]",
    header: "header flex flex-col gap-4 md:gap-2",
    content: "content grow",
    title: "text-lg font-semibold",
    label: "inline-flex items-center gap-1 text-sm font-semibold",
    actions:
      "flex flex-col-reverse gap-3 pt-4 *:w-full @lg:col-start-2 @lg:row-span-full @lg:flex-row @lg:self-center @lg:justify-self-end @lg:pt-0 @lg:*:w-fit",
  },
  variants: {
    color: {
      primary: {
        root: "bg-primary-100 text-primary-950",
      },
      secondary: {
        root: "bg-secondary-100 text-secondary-950",
      },
      neutral: {
        root: "bg-neutral-50 text-neutral-800",
      },
    },
  },
});

const { root, content, header, title, actions, label, container } = styles();

const BannerVariantsContext =
  createContext<VariantProps<typeof styles>["color"]>("primary");

const useBannerVariantsContext = () => {
  const color = useContext(BannerVariantsContext);
  return { color };
};

const Root = ({
  children,
  className,
  color = "primary",
  ...props
}: ComponentProps<"div"> & VariantProps<typeof styles>) => {
  return (
    <BannerVariantsContext.Provider value={color}>
      <div className={root({ color })} {...props}>
        <div className={container({ className })}>{children}</div>
      </div>
    </BannerVariantsContext.Provider>
  );
};

const Content = ({ className, ...props }: ComponentProps<"div">) => {
  const { color } = useBannerVariantsContext();

  return <div className={content({ className, color })} {...props} />;
};

const Header = ({ className, ...props }: ComponentProps<"header">) => {
  const { color } = useBannerVariantsContext();

  return <header className={header({ className, color })} {...props} />;
};

const Title = ({ className, children, ...props }: ComponentProps<"h3">) => {
  const { color } = useBannerVariantsContext();

  return (
    <h3 className={title({ className, color })} {...props}>
      {children}
    </h3>
  );
};

const Label = ({ className, children, ...props }: ComponentProps<"label">) => {
  return (
    <label className={label({ className })} {...props}>
      {children}
    </label>
  );
};

const Description = ({ ...props }: ComponentProps<"p">) => {
  return <p {...props} />;
};

const Actions = ({ className, ...props }: ComponentProps<"div">) => {
  return <div className={actions({ className })} {...props} />;
};

export const Banner = {
  Root,
  Content,
  Title,
  Label,
  Description,
  Actions,
  Header,
};
